import { createBrowserRouter, RouterProvider, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { ServiceAuthGate, AdminAuthGate, AuthCallback } from "./auth/AuthGate";
import { Home, Children, ChildDetail, AssignPractice, ProgressExport, LinkAChild, SharingConsent, ServiceSettings } from "./caregiver";
import { Overview, Members, FamilyLicences, Billing, Kids, ConsentPrivacy, DataRights, OrgSettings } from "./admin";
import { SalesGate } from "./sales/SalesPortal";
import { portalMode, otherPortalOrigin, type PortalMode } from "../lib/host";

/**
 * Sends the browser to the other portal's origin, preserving the rest of the
 * path. Used on the production hosts, where each domain serves exactly one
 * portal: asking portal.zooylo.app for /admin/members lands you on
 * admin.zooylo.app/members instead of a dead route.
 *
 * `replace` rather than assign so the dead URL does not sit in history and trap
 * the back button in a redirect loop.
 */
function CrossOriginRedirect({
  mode,
  to,
}: {
  /**
   * The mode this route tree was BUILT for — passed in, never re-read from the
   * module-level `portalMode()`. The two agree in production (both derive from
   * the same hostname), but a component that silently consults a global instead
   * of the tree it belongs to cannot be tested and cannot be reasoned about: a
   * tree built for "caregiver" would compute its redirect target from whatever
   * host the bundle happened to boot on.
   */
  mode: PortalMode;
  to: (pathname: string) => string;
}) {
  const location = useLocation();
  const origin = otherPortalOrigin(
    mode,
    import.meta.env as { VITE_ADMIN_ORIGIN?: string; VITE_CAREGIVER_ORIGIN?: string },
  );
  const target = origin ? `${origin}${to(location.pathname)}${location.search}` : null;

  useEffect(() => {
    if (target) window.location.replace(target);
  }, [target]);

  // Rendered for the instant before the navigation commits, and permanently for
  // anyone with JS-driven navigation blocked — so give them a real link.
  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 32, textAlign: "center", fontFamily: "var(--font-body)" }}>
      <p style={{ color: "var(--ink-soft)", fontSize: 16.5 }}>
        Taking you to <a href={target ?? "/"} style={{ color: "var(--brand)" }}>{target ?? "the portal"}</a>…
      </p>
    </div>
  );
}

/** /admin/* on the caregiver host → the same page on the admin host. */
function ToAdminHost({ mode }: { mode: PortalMode }) {
  return (
    <CrossOriginRedirect
      mode={mode}
      to={(p) => p.replace(/^\/admin(?=\/|$)/, "") || "/"}
    />
  );
}

/** A caregiver path on the admin host → the same page on the caregiver host. */
function ToCaregiverHost({ mode }: { mode: PortalMode }) {
  return <CrossOriginRedirect mode={mode} to={(p) => p} />;
}

/**
 * Legacy in-app redirect: /admin/members → /members on the admin host, for links
 * minted before the split. Keeps deep links from old emails working.
 */
function LegacyAdminPath() {
  const location = useLocation();
  const stripped = location.pathname.replace(/^\/admin(?=\/|$)/, "") || "/";
  return <Navigate to={`${stripped}${location.search}`} replace />;
}

const caregiverChildren = [
  { index: true, element: <Home /> },
  { path: "children", element: <Children /> },
  { path: "children/:childId", element: <ChildDetail /> },
  { path: "assign", element: <AssignPractice /> },
  { path: "export", element: <ProgressExport /> },
  { path: "link", element: <LinkAChild /> },
  { path: "consent", element: <SharingConsent /> },
  { path: "settings", element: <ServiceSettings /> },
];

const adminChildren = [
  { index: true, element: <Overview /> },
  { path: "kids", element: <Kids /> },
  { path: "licences", element: <FamilyLicences /> },
  { path: "billing", element: <Billing /> },
  { path: "members", element: <Members /> },
  // Retired screens: staff seats are free (seats → Users), sponsored families
  // are family licences, and roster oversight is the Children screen. Old links
  // keep working.
  { path: "seats", element: <Navigate to="../members" replace /> },
  { path: "sponsored", element: <Navigate to="../licences" replace /> },
  { path: "roster", element: <Navigate to="../kids" replace /> },
  { path: "consent", element: <ConsentPrivacy /> },
  { path: "data", element: <DataRights /> },
  { path: "settings", element: <OrgSettings /> },
];

/**
 * Builds the route tree for the given host mode.
 *
 *   "admin"     — admin portal at "/", caregiver paths bounce to portal.zooylo.app
 *   "caregiver" — caregiver portal at "/", /admin/* bounces to admin.zooylo.app
 *   "both"      — the original tree (localhost + preview deploys)
 *
 * The magic-link landing routes must sit ABOVE the portal layouts so the exact
 * path wins: they establish the session from the URL, then redirect inward.
 */
export function buildRoutes(mode: PortalMode) {
  if (mode === "admin") {
    return [
      { path: "/auth/callback", element: <AuthCallback to="/" /> },
      // Links minted before the split still carry /admin/... — honour them.
      { path: "/admin/auth/callback", element: <AuthCallback to="/" /> },
      { path: "/admin", element: <LegacyAdminPath /> },
      { path: "/admin/*", element: <LegacyAdminPath /> },
      // The owner console grew into the Sales portal; old links keep working.
      { path: "/console", element: <Navigate to="/sales" replace /> },
      { path: "/sales/*", element: <SalesGate /> },
      {
        path: "/",
        element: <AdminAuthGate />,
        children: adminChildren,
      },
      // Caregiver-only paths that do not collide with an admin route.
      { path: "/children", element: <ToCaregiverHost mode="admin" /> },
      { path: "/children/*", element: <ToCaregiverHost mode="admin" /> },
      { path: "/assign", element: <ToCaregiverHost mode="admin" /> },
      { path: "/export", element: <ToCaregiverHost mode="admin" /> },
      { path: "/link", element: <ToCaregiverHost mode="admin" /> },
    ];
  }

  if (mode === "caregiver") {
    return [
      { path: "/auth/callback", element: <AuthCallback to="/" /> },
      { path: "/admin/auth/callback", element: <ToAdminHost mode="caregiver" /> },
      { path: "/admin", element: <ToAdminHost mode="caregiver" /> },
      { path: "/admin/*", element: <ToAdminHost mode="caregiver" /> },
      { path: "/console", element: <ToAdminHost mode="caregiver" /> },
      { path: "/sales", element: <ToAdminHost mode="caregiver" /> },
      { path: "/sales/*", element: <ToAdminHost mode="caregiver" /> },
      {
        path: "/",
        element: <ServiceAuthGate />,
        children: caregiverChildren,
      },
    ];
  }

  return [
    { path: "/auth/callback", element: <AuthCallback to="/" /> },
    { path: "/admin/auth/callback", element: <AuthCallback to="/admin" /> },
    { path: "/console", element: <Navigate to="/sales" replace /> },
    { path: "/sales/*", element: <SalesGate /> },
    { path: "/", element: <ServiceAuthGate />, children: caregiverChildren },
    { path: "/admin", element: <AdminAuthGate />, children: adminChildren },
  ];
}

const router = createBrowserRouter(buildRoutes(portalMode()));

export function AppRouter() {
  return <RouterProvider router={router} />;
}
